import { FormFieldProps, FormVmItemConfigType, InitialData } from '../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../infrastructure/implementations/form-viewmodel-controller';
import { action, observable } from 'mobx';
import { TObject } from '../../../../../infrastructure/type-extensions';
import { IChildConnectionDTO, IConnectionDetailsDTO, IConnectionsDAO } from '../../../../../api/connections.dao';
import * as IoC from 'typescript-ioc';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IEventRequestParams } from '../../../../../api/shared.dto';

export class ChildConnectionFormVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    protected static createFormVM(configList: FormVmItemConfigType[], initialData: InitialData): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'ChildConnection', configList);
            }
        }();
    }

    @observable
    protected formDataCtrl: AbstractFormViewModelController;

    @observable
    private loading: boolean = false;

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        public readonly childConnection: IChildConnectionDTO,
        public readonly parentConnectionDetails: IConnectionDetailsDTO,
        private readonly onChildConnectionUpdated: (childConnection: IChildConnectionDTO, parentConnectionId: number) => void,
        @IoC.Inject private readonly connectionDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO)
    ) {
    }

    @action
    protected initFormDataCtrl(configList: FormVmItemConfigType[], initialData: InitialData): void {
        this.formDataCtrl = ChildConnectionFormVM.createFormVM(configList, initialData);
    }

    public async updateChildConnection(child: Partial<IChildConnectionDTO>): Promise<boolean> {
        if (this.formDataCtrl.isVmValid()) {
            return this.callUpdateChildConnectionApi(child);
        } else {
            this.formDataCtrl.setVmTouched();
        }
    }

    public getFormFields(keys: TObject<string>): TObject<FormFieldProps> {
        const formFields = {};

        for (const key of Object.values(keys)) {
            formFields[key] = this.formDataCtrl.getFormVmValuePropsByKey(key);
        }

        return formFields;
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    @action.bound
    private startLoading(): void {
        this.loading = true;
    }

    @action.bound
    private stopLoading(): void {
        this.loading = false;
    }

    @action.bound
    private async callUpdateChildConnectionApi(child: Partial<IChildConnectionDTO>): Promise<boolean> {
        this.startLoading();
        try {
            const response = await this.connectionDAO.updateChildConnection(this.composeApiParams(child));
            this.onChildConnectionUpdated(response, this.parentConnectionDetails.meetingConnectionId);
            return true;
        } catch (err) {
            ChildConnectionFormVM.handleApiException(err);
            return false;
        } finally {
            this.stopLoading();
        }
    }

    private composeApiParams(child: Partial<IChildConnectionDTO>): IEventRequestParams & Partial<IChildConnectionDTO> {
        return {
            ...child,
            accountId: this.accountId,
            eventId: this.eventId,
        };
    }
}